import React, { useState, useRef } from 'react'
import { Calendar } from '@amir04lm26/react-modern-calendar-date-picker'
import '@amir04lm26/react-modern-calendar-date-picker/lib/DatePicker.css'
import View from './View'

interface DatePickerProps {
  label: string
  value: string
  onChange: (value: string) => void
  containerStyles?: React.CSSProperties
  error?: boolean
}

interface DayValue {
  year: number
  month: number
  day: number
}

const DatePicker = ({ label, value, onChange, containerStyles, error }: DatePickerProps): JSX.Element => {
  const [isOpen, setIsOpen] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const parseDate = (dateString: string): DayValue | null => {
    if (dateString === '') return null
    const date = new Date(`${dateString}T00:00:00`)
    if (isNaN(date.getTime())) return null
    return {
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      day: date.getDate()
    }
  }

  const formatDate = (dayValue: DayValue | null): string => {
    if (dayValue === null) return ''
    const date = new Date(dayValue.year, dayValue.month - 1, dayValue.day)
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  const selectedDay = parseDate(value)

  const handleDayChange = (dayValue: DayValue | null): void => {
    const dateString = formatDate(dayValue)
    onChange(dateString)
    setIsOpen(false)
  }

  const handleInputClick = (): void => {
    setIsOpen(!isOpen)
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    onChange(e.target.value)
  }

  const initialLabelStyles = 'top-3 focus:top:1 active:top:1'
  const focusedLabelStyles = 'top-1 text-[13px]'
  const initialInputStyles = 'pt-4 pr-2 pl-4 pb-4'
  const focusedInputStyles = 'pt-8 pr-2 pl-4 pb-4'
  const labelStyles = value.length > 0 ? focusedLabelStyles : initialLabelStyles
  const inputStyles = value.length > 0 ? focusedInputStyles : initialInputStyles

  return (
    <View className="relative w-full" style={containerStyles}>
      <input
        className={`rounded-4 h-[47px] text-xl outline-none box-border w-[100%] focus:shadow-[0px_0px_5px_rgba(0,0,0,0.3)] ${inputStyles} ${
          error === true ? 'border-2 border-errorRed' : ''
        }`}
        value={value}
        onChange={handleInputChange}
        onClick={handleInputClick}
        ref={inputRef}
        readOnly
      />
      <label className={`absolute flex left-4 ${labelStyles}`} onClick={handleInputClick}>
        {label}
      </label>

      {isOpen && (
        <View className="absolute top-full left-0 z-50 mt-2 rounded-4 shadow-bothMedium border border-lightGrey">
          <Calendar
            value={selectedDay}
            onChange={handleDayChange}
            shouldHighlightWeekends
            colorPrimary="#7FC324"
            colorPrimaryLight="rgba(127, 195, 36, 0.1)"
          />
        </View>
      )}

      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => {
            setIsOpen(false)
          }}
        />
      )}
    </View>
  )
}

export default DatePicker
